import { dumpLogs } from "@modules/home/hooks/dumpLogs";
import { FigmaJsonStates } from "@modules/home/view/activity/Components/Blocks/AllInOne/FigmaStates";
import { captureException } from "@sentry/nextjs";
import {
    createAudioKit,
    type AudioKit,
    type PlayResult,
    type SoundItem,
} from "./audioKit";

class Sound {
    private audioKit!: AudioKit;
    private sounds: Record<
        string,
        {
            path: string;
            url: string;
            blob?: string;
            loaded: boolean;
            audio?: HTMLAudioElement | null;
            props?: any;
            duration: number | null;
        }
    > = {};
    showPopup = () => {};
    removePopup = () => {};
    onError = (_e: any) => {};
    pauseSound = false;
    questionAudios: any[] = [];
    private pausedAudioId: string | null = null;
    private lastPlayTime = 0;

    constructor() {
        // Only initialize in browser environment
        if (typeof window !== "undefined") {
            this.initializeAudioKit();
            this.handleAudioInterruption();
        } else {
            // Create a mock audioKit for SSR
            this.audioKit = createAudioKit({});
        }
    }

    private initializeAudioKit() {
        this.audioKit = createAudioKit({
            playMode: "mono",
            cacheName: "mathai-audio-v1",
            logger: {
                debug: (msg, ctx) =>
                    dumpLogs({ action: "audio_debug", msg, ...ctx }),
                info: (msg, ctx) =>
                    dumpLogs({ action: "audio_info", msg, ...ctx }),
                warn: (msg, ctx) =>
                    dumpLogs({ action: "audio_warn", msg, ...ctx }),
                error: (msg, ctx) =>
                    dumpLogs({ action: "audio_error", msg, ...ctx }),
            },
            telemetry: {
                event: (name, props) =>
                    dumpLogs({
                        action: name,
                        pauseSound: this.pauseSound,
                        ...props,
                    }),
            },
            sentry: {
                captureException: (e, ctx) => captureException(e, ctx),
            },
            retry: {
                retries: 3,
                baseDelayMs: 300,
                maxDelayMs: 5000,
                jitter: true,
                timeoutMs: 12000,
            },
            cooldown: {
                globalMinIntervalMs: 80,
                perIdMinIntervalMs: {},
                onDrop: (id) => {
                    dumpLogs({
                        action: "audio_dropped_cooldown",
                        sound_name: id,
                        pauseSound: this.pauseSound,
                    });
                },
            },
            memory: {
                maxDecodedBytes: 32 * 1024 * 1024,
                maxBuffers: 256,
            },
            transform: {
                downmixToMono: true, // Optimize for mobile
            },
            preempt: {
                minTailMs: 200, // Protect last 200ms of audio
                dropOnTail: true, // Don't interrupt endings
            },
        });
    }

    private handleAudioInterruption() {
        if (typeof window == "undefined") return;

        // For iOS - check both platform and userAgent for maximum compatibility
        const isIOS = (() => {
            // Check userAgent first (more reliable on newer devices)
            if (navigator.userAgent.match(/(iPhone|iPod|iPad)/i)) return true;

            // Fallback to platform for older devices (despite deprecation)
            try {
                if (
                    navigator.platform &&
                    navigator.platform.match(/(iPhone|iPod|iPad)/i)
                )
                    return true;
            } catch (e) {
                // Ignore if platform is not available
            }

            // Additional check for iPadOS 13+ requesting desktop site
            if (
                navigator.platform === "MacIntel" &&
                navigator.maxTouchPoints > 1
            )
                return true;

            return false;
        })();

        if (isIOS) {
            document.addEventListener("visibilitychange", () => {
                if (document.hidden) {
                    dumpLogs({
                        action: "sound_pause_visibility_change",
                        pauseSound: this.pauseSound,
                    });
                    this.pause();
                } else {
                    // Optionally resume playback when the app comes back to foreground
                    // this.resume();
                }
            });

            window.addEventListener("pagehide", () => {
                dumpLogs({
                    action: "sound_pause_page_hide",
                    pauseSound: this.pauseSound,
                });
                this.pause();
            });

            // Periodic check for unexpected pauses
            setInterval(() => {
                const audioElement = document.getElementById(
                    "interactionSoundComponent",
                ) as HTMLAudioElement;

                if (audioElement && !audioElement.paused && document.hidden) {
                    dumpLogs({
                        action: "sound_pause_periodic",
                        pauseSound: this.pauseSound,
                    });
                    this.pause();
                }
            }, 1000); // Check every second
        }

        // For Android - check both userAgent and platform for compatibility
        const isAndroid = (() => {
            // Primary check via userAgent
            if (/Android/i.test(navigator.userAgent)) return true;

            // Fallback to platform for some older Android devices
            try {
                if (
                    navigator.platform &&
                    /Linux/i.test(navigator.platform) &&
                    /Android/i.test(navigator.userAgent)
                )
                    return true;
            } catch (e) {
                // Ignore if platform is not available
            }

            return false;
        })();

        if (isAndroid) {
            window.addEventListener("blur", () => {
                dumpLogs({
                    action: "sound_pause_blur",
                    pauseSound: this.pauseSound,
                });
                this.pause();
            });

            // Optional: resume playback when focus is regained
            window.addEventListener("focus", () => {
                // this.resume();
            });
        }

        // General approach for modern browsers
        if ("mediaSession" in navigator) {
            navigator.mediaSession.setActionHandler("pause", () => {
                dumpLogs({
                    action: "sound_pause_action",
                    pauseSound: this.pauseSound,
                });
                this.pause();
            });
        }

        // Handle audio context state changes
        try {
            if (window?.AudioContext || (window as any)?.webkitAudioContext) {
                const audioContext = new (window?.AudioContext ||
                    (window as any)?.webkitAudioContext)();
                audioContext.onstatechange = () => {
                    if (audioContext.state === "interrupted") {
                        dumpLogs({
                            action: "sound_pause_interrupt",
                            pauseSound: this.pauseSound,
                        });
                        this.pause();
                    } else if (audioContext?.state === "running") {
                        // Optionally resume playback
                        // this.resume();
                    }
                };
            }
        } catch (e) {
            captureException(e);
        }
    }
    resetSrc = (audioElement: HTMLAudioElement) => {
        // Kept for backward compatibility but no longer used with audioKit
        if (!audioElement) return;
        const events = [
            "onerror",
            "onabort",
            "oncancel",
            "onclose",
            "onstalled",
            "onloadstart",
            "ondurationchange",
            "onloadeddata",
            "onprogress",
            "oncanplay",
            "oncanplaythrough",
            "onloadedmetadata",
            "onended",
            "onpause",
            "onplay",
        ];
        for (const event of events) {
            (audioElement as any)[event] = null;
        }
        audioElement.src = "";
    };
    async preloadAudio(name: string, url: string, props?: any) {
        dumpLogs({
            action: "preload_audio_start",
            sound_name: name,
            sound_url: url,
            sound: this.sounds[name],
            pauseSound: this.pauseSound,
        });

        // Store metadata in our local cache
        if (!this.sounds[name]) {
            this.sounds[name] = {
                path: url,
                url,
                loaded: false,
                audio: null,
                duration: null,
                props,
            };
        }

        // Preload using audioKit
        const soundItem: SoundItem = { id: name, url };
        try {
            await this.audioKit.preloadCritical([soundItem], {
                onProgress: (progress, id) => {
                    if (id === name && progress === 1) {
                        if (this.sounds[name]) {
                            this.sounds[name].loaded = true;
                        }
                        props?.loaded?.();
                        dumpLogs({
                            action: "preload_audio_can_play_through",
                            sound_name: name,
                            sound_url: url,
                            sound: this.sounds[name],
                            pauseSound: this.pauseSound,
                        });
                    }
                },
                onError: (id, error) => {
                    dumpLogs({
                        action: "preload_audio_fail",
                        sound_name: name,
                        sound: this.sounds[name],
                        error,
                        pauseSound: this.pauseSound,
                    });
                    this.onError?.({
                        msg: "PRELOAD_ERROR",
                        error,
                        id,
                    });
                    props?.setDuration?.(5);
                },
            });

            // Try to get duration (audioKit doesn't expose this directly)
            // We'll set a default or estimate
            const duration = 5; // Default duration
            if (this.sounds[name]) {
                this.sounds[name].duration = duration;
            }
            props?.setDuration?.(duration);
        } catch (e) {
            dumpLogs({
                sound_url: url,
                action: "preload_audio_fail_fetch",
                sound_name: name,
                sound: this.sounds[name],
                error: e,
                pauseSound: this.pauseSound,
            });
            captureException(e);
        }
    }
    add(name: string, path: string, props?: any) {
        if (this.sounds[name] && this.sounds[name].path == path) {
            props?.setDuration &&
                props?.setDuration?.(this.sounds[name].duration);
            return;
        }
        this.sounds[name] = {
            path,
            url: path,
            loaded: false,
            audio: null,
            duration: null,
            props,
        };
        this.preloadAudio(name, path, props);
    }
    pauseIfPopupIsEnabled() {
        // pause if popup-layout is visible
        const div = document.getElementById("popup-layout");
        if (div) {
            dumpLogs({
                action: "audio_pause_if_popup_is_enabled",
                pauseSound: this.pauseSound,
            });
            this.pause();
        }
    }
    getSoundName(audioElement: HTMLAudioElement): string {
        const url = decodeURIComponent(audioElement.src);
        const isBlob = url.includes("blob");
        return (
            Object.keys(this.sounds).find(
                (i) => this.sounds[i]?.[isBlob ? "blob" : "url"] == url,
            ) ?? ""
        );
    }

    async play(
        name: string,
        callbacks: {
            interaction?: boolean;
            complete?: () => void;
            onPlay?: () => void;
            error?: (arg0: String, ...rest: any) => void;
            onSkip?: () => void;
        } = {},
    ) {
        dumpLogs({
            action: "play_audio_start",
            sound_name: name,
            sound: this.sounds[name],
            pauseSound: this.pauseSound,
        });

        // Check if paused
        if (this.pauseSound) {
            this.showPopup();
            callbacks.onSkip?.();
            return;
        }

        // Check if sound exists
        if (!this.sounds[name] && !this.audioKit.has(name)) {
            dumpLogs({
                action: "play_audio_not_found",
                sound_name: name,
                sound: this.sounds,
                pauseSound: this.pauseSound,
            });
            callbacks.error?.("NOT_FOUND");
            return;
        }

        try {
            // Handle popup if needed
            this.pauseIfPopupIsEnabled();

            // Remove popup on play start
            if (callbacks.interaction) {
                this.removePopup();
            }

            callbacks.onPlay?.();
            this.lastPlayTime = Date.now();

            // Play using audioKit
            const result: PlayResult = await this.audioKit.playAndWait(name, {
                volume: 1,
                maxMs: 30000, // 30 second timeout
            });

            dumpLogs({
                action: `play_audio_${result.status}`,
                sound_name: name,
                sound: this.sounds[name],
                pauseSound: this.pauseSound,
                result,
            });

            switch (result.status) {
                case "ok":
                    callbacks.complete?.();
                    break;
                case "timeout":
                    callbacks.error?.("TIMEOUT");
                    this.onError?.({ msg: "TIMEOUT", id: name });
                    break;
                case "missing":
                    callbacks.error?.("NOT_FOUND");
                    break;
                case "error":
                    if (
                        String(result.error).includes(
                            "interact with the document",
                        )
                    ) {
                        callbacks.error?.("NO_TAP");
                        this.onError?.("NO_TAP");
                    } else {
                        callbacks.error?.("PLAY_ERROR", result.error);
                        this.onError?.({
                            msg: "PLAY_ERROR",
                            error: result.error,
                        });
                    }
                    break;
                case "preempted":
                case "dropped":
                    callbacks.onSkip?.();
                    break;
            }
        } catch (e) {
            dumpLogs({
                action: "play_audio_error",
                sound_name: name,
                error: e,
                pauseSound: this.pauseSound,
            });
            captureException(e);
            callbacks.error?.("FAILED", e);
        }
    }

    async playDirect(
        url: string,
        callbacks: {
            interaction?: boolean;
            complete?: () => void;
            onPlay?: () => void;
            error?: (arg0: String, ...rest: any) => void;
            onSkip?: () => void;
        },
    ) {
        if (!url) {
            callbacks.error?.("ERROR");
            return;
        }

        dumpLogs({
            action: "direct_play_audio_start",
            sound_url: url,
            pauseSound: this.pauseSound,
        });

        // Generate a unique ID for this direct play
        const directId = `direct_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

        try {
            // Preload the URL first
            await this.audioKit.preloadCritical([{ id: directId, url }]);

            // Then play it
            callbacks.onPlay?.();
            if (callbacks.interaction) {
                this.removePopup();
            }

            const result = await this.audioKit.playAndWait(directId, {
                volume: 1,
                maxMs: 30000,
            });

            dumpLogs({
                action: `direct_play_audio_${result.status}`,
                sound_url: url,
                pauseSound: this.pauseSound,
                result,
            });

            if (result.status === "ok") {
                callbacks.complete?.();
            } else if (result.status === "error") {
                if (
                    String(result.error).includes("interact with the document")
                ) {
                    callbacks.error?.("NO_TAP");
                    this.onError?.("NO_TAP");
                } else {
                    callbacks.error?.("ERROR", result.error);
                }
            } else if (result.status === "timeout") {
                callbacks.error?.("TIMEOUT");
            }

            // Clean up after playing
            // audioKit will handle cache eviction automatically via LRU
        } catch (e) {
            dumpLogs({
                action: "direct_play_audio_error",
                sound_url: url,
                error: e,
                pauseSound: this.pauseSound,
            });
            captureException(e);
            callbacks.error?.("ERROR", e);
        }
    }
    find(name: string) {
        return this.sounds[name];
    }

    // Public method to access audioKit for blessing
    getAudioKit(): AudioKit {
        // Ensure audioKit is properly initialized in browser
        if (typeof window !== "undefined" && !this.audioKit) {
            this.initializeAudioKit();
        }
        return this.audioKit;
    }
    pauseCallback() {
        const audioElement = document.getElementById(
            "interactionSoundComponent",
        ) as HTMLAudioElement;
        const timeout = setTimeout(() => {
            const div = document.getElementById("popup-layout");
            if (
                audioElement.paused &&
                !audioElement.ended &&
                !div &&
                !document.hidden
            ) {
                const sound_name = this.getSoundName(audioElement);
                const playCatch = (e: any) => {
                    this.onError("NO_TAP");
                    if (
                        typeof window != "undefined" &&
                        audioElement.src == window.location.href
                    ) {
                        return;
                    }
                    if (
                        String(e).includes(
                            "The element has no supported sources.",
                        )
                    ) {
                        return;
                    }
                    if (
                        String(e).includes("interact with the document first")
                    ) {
                        dumpLogs({
                            action: "play_audio_no_tap",
                            sound_name,
                            sound: this.sounds[sound_name],
                            pauseSound: this.pauseSound,
                        });
                    } else {
                        dumpLogs({
                            action: "play_audio_play_error_pause_callback",
                            sound_name,
                            sound: this.sounds[sound_name],
                            eventPointer: e,
                            error: e?.message,
                            tError: e?.target?.error,
                            tErrorMsg: e?.target?.error?.message,
                            pauseSound: this.pauseSound,
                        });
                    }
                    captureException(e);
                };
                dumpLogs({
                    action: "play_audio_auto_unpause",
                    sound_url: audioElement.src,
                    sound_name: this.getSoundName(audioElement),
                    pauseSound: this.pauseSound,
                });
                audioElement.play().catch(playCatch);
                this.pauseSound = false;
            }
            clearTimeout(timeout);
        }, 1000);
    }
    pause(name: string = "") {
        this.pauseSound = true;

        // Stop current playback
        this.audioKit.stopAll();

        // Store which audio was paused if name provided
        if (name) {
            this.pausedAudioId = name;
        }

        dumpLogs({
            action: "play_audio_pause",
            sound_name: name || this.pausedAudioId,
            pauseSound: this.pauseSound,
        });

        this.showPopup();
        this.pauseCallback();
    }
    async resume(name: string = "") {
        this.pauseSound = false;

        try {
            // Resume audio context if suspended
            await this.audioKit.resume();

            const soundToResume = name || this.pausedAudioId;

            dumpLogs({
                action: "play_audio_resume",
                sound_name: soundToResume,
                pauseSound: this.pauseSound,
            });

            // If we have a specific sound to resume, try playing it
            if (soundToResume && this.audioKit.has(soundToResume)) {
                this.play(soundToResume).catch((e) => {
                    if (String(e).includes("interact with the document")) {
                        dumpLogs({
                            action: "play_audio_no_tap",
                            sound_name: soundToResume,
                            pauseSound: this.pauseSound,
                        });
                        this.onError?.("NO_TAP");
                    } else {
                        dumpLogs({
                            action: "play_audio_play_error_resume",
                            sound_name: soundToResume,
                            error: e,
                            pauseSound: this.pauseSound,
                        });
                        this.onError?.({
                            msg: "RESUME_ERROR",
                            error: e,
                        });
                    }
                    captureException(e);
                });
            }

            this.pausedAudioId = null;
        } catch (e) {
            dumpLogs({
                action: "audio_resume_error",
                error: e,
                pauseSound: this.pauseSound,
            });
            captureException(e);
        }
    }
    stop(name: string) {
        // Since audioKit is in mono mode, we can only stop if it's currently playing
        // We don't have a way to know what's currently playing, so we'll stop all
        dumpLogs({
            action: "play_audio_stop",
            sound_name: name,
            pauseSound: this.pauseSound,
        });

        // Stop all audio (mono mode only has one voice)
        this.audioKit.stopAll();
    }

    stopAll() {
        dumpLogs({
            action: "play_audio_stop_all",
            pauseSound: this.pauseSound,
        });

        this.audioKit.stopAll();
    }
    forceEnd() {
        dumpLogs({
            action: "play_audio_force_end",
            pauseSound: this.pauseSound,
        });

        // Stop current audio immediately
        this.audioKit.stopAll();
        this.pauseSound = false;
    }
    removeAll(exceptions: string[] = []) {
        dumpLogs({
            action: "audio_remove_all",
            sounds: this.sounds,
            pauseSound: this.pauseSound,
        });

        // Store exceptions
        const countDownSound = this.sounds["Count-down-sound"];
        const exceptionAudios = exceptions?.reduce?.((acc: any, name) => {
            if (this.sounds[name]) {
                acc[name] = this.sounds[name];
            }
            return acc;
        }, {});

        // Clear audioKit cache
        this.audioKit.clear();

        // Reset sounds tracking but keep exceptions
        this.sounds = {
            "Count-down-sound": countDownSound,
            ...exceptionAudios,
        };
        this.questionAudios = [];

        // Re-preload exception sounds
        const soundsToReload = Object.keys(this.sounds).filter(
            (name) => this.sounds[name],
        );
        if (soundsToReload.length > 0) {
            const items: SoundItem[] = soundsToReload.map((name) => ({
                id: name,
                url: this.sounds[name].url,
            }));
            this.audioKit.preloadCritical(items).catch((e) => {
                dumpLogs({
                    action: "audio_remove_all_reload_error",
                    error: e,
                    sounds: soundsToReload,
                });
            });
        }
    }
    removeOne(name: string) {
        dumpLogs({
            action: "audio_remove_one",
            sounds: this.sounds,
            name,
            pauseSound: this.pauseSound,
        });

        if (this.sounds[name]) {
            delete this.sounds[name];
        }

        // Note: audioKit doesn't have a removeOne method,
        // but the LRU cache will naturally evict it when needed
    }

    addQuestionAudiosList() {
        this.questionAudios =
            FigmaJsonStates.getNonUpdatedJson("question_audios");
    }
}

export const SoundState = new Sound();
